import { openDrawer } from "@/src/components/dialogs";

import { Button } from "@/src/components/buttons";
import { Card, CompartmentCard } from "@/src/components/cards";
import { FetchableList } from "@/src/components/fetchableLists";
import { Input } from "@/src/components/universalInput";
import {
  Diagnostic,
  FieldOption,
  FolderItem,
  Patient,
  Reference,
} from "@/src/data/allTypes";
import apiFetch from "@/src/utils/apiFetch";
import { getItemId, getUserId, getUserShortName, uid } from "@/src/utils/utils";
import { useState } from "react";
import { ReferenceForm } from "../admin/referentiel/referenceForm";
import { ReferenceFieldsForm } from "../fields/referenceFieldsForm";
import { getStandardConfidentiality } from "../patients/confidentialitiesHook";
import { openSnomedConceptPicker } from "../snomed/SnomedConceptPicker";
import { openDiagnosticDrawer } from "./diagnosticDrawer";
import { ExamsCompartment } from "./examsCompartment";
import { MetastasisCompartment } from "./metastasisCompartment";
import { StoryCompartment } from "./storyCompartment";

function DiagnosticsCompartment({
  patient,
  parent,
}: {
  patient: Patient;
  parent: FolderItem | null;
}) {
  const [reload, setReload] = useState(0);

  async function openDiagnosticChoiceDrawer(diagnostic: Diagnostic) {
    await openSnomedConceptPicker({
      title: "Choisir un diagnostic",
      parentIds: ["cancer"],
      category: "diagnostic",
      onSelect: async (reference) => {
        diagnostic.reference = reference;
        await openDrawer({
          title: "Diagnostic",
          content: (
            <div className="c1 gap10">
              <ReferenceFieldsForm
                patient={patient}
                reference={diagnostic.reference}
                currentAnswers={diagnostic.content!.fieldAnswers || []}
                onChange={(newValue) => {
                  diagnostic.content!.fieldAnswers = newValue;
                }}
              />
              <Input
                name="Notes"
                value={diagnostic.notes}
                type="textarea"
                rows={5}
                onChange={(newValue) => {
                  diagnostic.notes = newValue;
                }}
              />
            </div>
          ),
          onConfirm: async () => {
            diagnostic = await apiFetch("diagnostics", "addOne", {
              diagnostic,
            });
            setReload((prev) => prev + 1);
            await openDiagnosticDrawer({
              patient,
              diagnostic,
              onSave: (newValue) => {
                diagnostic = newValue;
                setReload((prev) => prev + 1);
              },
            });
          },
        });
      },
    });

    // await openDrawer({
    //   id: "diagnosticChoiceDrawer",
    //   title: "Choisir Diagnostic",
    //   content: (
    //     <DiagnosticsList
    //       onSelect={async (reference) => {
    //         closeDrawer("diagnosticChoiceDrawer");
    //         item.reference = reference;
    //         await openDiagnosticDrawer(item);
    //       }}
    //     />
    //   ),
    // });
  }

  // async function openDiagnosticDrawer(item: Diagnostic) {
  //   if (!item?.reference) return;
  //   item.content ||= {
  //     reference: item.reference,
  //     fieldAnswers: [],
  //     creator: getUserId(),
  //     patient: patient,
  //   };
  //   await openDrawer({
  //     id: "diagnosticDrawer",
  //     title: "Diagnostic",
  //     content: (
  //       <ReferenceFieldsForm
  //         patient={patient}
  //         reference={item.reference}
  //         currentAnswers={item.content!.fieldAnswers || []}
  //         onChange={(newValue) => {
  //           item.content!.fieldAnswers = newValue;
  //         }}
  //       />
  //     ),
  //     onConfirm: async () => {
  //       let result = await apiFetch(
  //         "diagnostics",
  //         "addOne",
  //         { diagnostic: item },
  //         "Erreur lors de l'ajout du diagnostic"
  //       );
  //       item = result;
  //       await openExamsDrawer(item);
  //       await openStoryDrawer(item);
  //       setReload((prev) => prev + 1);
  //     },
  //   });
  // }

  async function openExamsDrawer(diagnostic: Diagnostic) {
    await openDrawer({
      id: "examsDrawer",
      title: "Examens lors du diagnostic",
      content: <ExamsCompartment patient={patient} parent={diagnostic} />,
    });
  }
  async function openMetastasisDrawer(diagnostic: Diagnostic) {
    await openDrawer({
      id: "metastasisDrawer",
      title: "Metastases",
      content: <MetastasisCompartment patient={patient} parent={diagnostic} />,
    });
  }

  async function openStoryDrawer(diagnostic: Diagnostic) {
    await openDrawer({
      id: "storyDrawer",
      title: "Histoire de la maladie",
      content: (
        <StoryCompartment
          patient={patient}
          parentId={getItemId(diagnostic)}
        ></StoryCompartment>
      ),
    });
  }

  return (
    <CompartmentCard
      title="Diagnostic(s)"
      onClick={async () => {
        let diagnostic: Diagnostic = {
          type: "diagnostic",
          reference: null,
          patient,
          exams: [],
          metastasis: [],
          story: null,
          creator: getUserId(),
          readBy: [getUserId()],

          confidentiality: getStandardConfidentiality(patient),
          content: {
            reference: null,
            fieldAnswers: [],
            creator: getUserId(),
            patient: patient,
          },
        };

        await openDiagnosticChoiceDrawer(diagnostic);
      }}
    >
      <FetchableList
        queryKey={["DiagnosticsCompartment", patient.id, reload]}
        queryFn={() =>
          apiFetch("diagnostics", "loadMany", {
            patientId: patient?.id || "",
            parentId: parent?.id,
          })
        }
      >
        {(data) => (
          <div className="c1 gap10">
            {data?.map((diagnostic) => {
              let reference = diagnostic.reference;

              return (
                <Card
                  key={uid()}
                  color="blue"
                  title={<div className="capitalize">{reference?.name}</div>}
                  onClick={async () => {
                    await openDiagnosticDrawer({
                      patient,
                      diagnostic,
                      onSave: (newValue) => {
                        diagnostic = newValue;
                        setReload((prev) => prev + 1);
                      },
                    });
                    // await openDiagnosticDrawer(diagnostic);
                  }}
                  onDelete={async () => {
                    await apiFetch(
                      "general",
                      "deleteAny",
                      {
                        table: "diagnostics",
                        id: diagnostic.id,
                      },
                      "Erreur lors de la suppression du diagnostic"
                    );
                    setReload((prev) => prev + 1);
                  }}
                >
                  <div className="gray">
                    {getUserShortName(diagnostic?.creator)}
                  </div>
                  {diagnostic.content?.fieldAnswers?.map((fieldAnswer) => {
                    let option = fieldAnswer?.value as FieldOption;
                    if (option?.id)
                      return (
                        <div key={uid()} className="gray">
                          {fieldAnswer?.field?.name}:{" "}
                          <strong className="black">
                            {option.id ? option.name : fieldAnswer?.value}
                          </strong>
                        </div>
                      );
                  })}

                  <div className="gray"> {diagnostic.notes} </div>
                  {/* <div className="c3 gap10">
                    <Button
                      type={"tertiary"}
                      onClick={async () => {
                        openStoryDrawer(diagnostic);
                      }}
                    >
                      Histoire
                    </Button>
                    <Button
                      type={"tertiary"}
                      onClick={async () => {
                        openExamsDrawer(diagnostic);
                      }}
                    >
                      Examens {diagnostic.exams?.length}
                    </Button>
                    <Button
                      type={"tertiary"}
                      onClick={async () => {
                        openMetastasisDrawer(diagnostic);
                      }}
                    >
                      Métastases
                    </Button>
                  </div> */}
                </Card>
              );
            })}
          </div>
        )}
      </FetchableList>
    </CompartmentCard>
  );
}

function DiagnosticsList({
  onSelect,
}: {
  onSelect: (reference: Reference) => void;
}) {
  const [reload, setReload] = useState(0);
  let [searchKey, setSearchKey] = useState("");

  return (
    <div className="grid c1 gap10">
      <FetchableList
        queryKey={["diagnosticsList", searchKey, reload]}
        queryFn={() =>
          apiFetch(
            "references",
            "loadMany",
            {
              userId: getUserId(),

              categoriesIds: ["diagnostic"],
              searchKey,
            },
            "Erreur lors du chargement des diagnostics"
          )
        }
        searchKey={searchKey}
        onSearch={(newValue) => {
          setSearchKey(newValue || "");
        }}
        emptyChild={
          <div className="grid c1 gap10">
            <div className="gray p20 center">
              Aucun diagnostic trouvé
              <strong>{searchKey && `content ${searchKey}`}</strong>
            </div>
            {searchKey && (
              <Button
                onClick={async () => {
                  let reference: Reference = {
                    name: searchKey,
                    category: "diagnostic",
                    creator: getUserId(),
                  };

                  await openDrawer({
                    id: "diagnosticFormDrawer",
                    title: "Créer Diagnostic",
                    content: (
                      <ReferenceForm
                        current={reference}
                        categories={["diagnostic"]}
                        onChange={(newValue) => {
                          reference = newValue;
                        }}
                      ></ReferenceForm>
                    ),
                    onConfirm: async () => {
                      let result = await apiFetch(
                        "references",
                        "addOne",
                        {
                          reference,
                        },
                        "Erreur lors de la création du diagnostic"
                      );
                      setReload((prev) => prev + 1);
                    },
                  });
                }}
              >
                Créer le diagnostic {searchKey}
              </Button>
            )}
          </div>
        }
      >
        {(data) => (
          <div className="grid c1 gap10">
            {data?.map((reference) => {
              return (
                <Card
                  key={uid()}
                  color="blue"
                  title={reference.name}
                  trailing={<div className="gray">{reference.cim10Code}</div>}
                  onClick={async () => {
                    onSelect(reference);
                  }}
                ></Card>
              );
            })}
          </div>
        )}
      </FetchableList>
    </div>
  );
}

export { DiagnosticsCompartment };
